/**
 * ProposalsBoardConfig — column definitions + bucketing rules for the
 * POS Proposals workflow (kanban) view.
 *
 * Stages are AUTO-DERIVED from existing proposal fields. There is no
 * `workflow_stage` column — bucketProposal() maps each row to a column id
 * based on status + activity timestamps. Direct stage moves and quick
 * actions land in v2.
 */

export interface WorkflowColumn {
  id: string;
  label: string;
  color: string;       // top border on the column header
  helpText: string;    // shown in empty state
}

export const WORKFLOW_COLUMNS: WorkflowColumn[] = [
  {
    id: 'drafting',
    label: 'Drafting',
    color: 'var(--color-text-light)',
    helpText: 'Drafts in progress. Includes admin-rejected proposals awaiting rework.',
  },
  {
    id: 'awaiting_review',
    label: 'Awaiting Review',
    color: 'var(--color-warning)',
    helpText: 'Submitted by an ambassador, waiting on admin approval.',
  },
  {
    id: 'sent',
    label: 'Sent',
    color: 'var(--color-info)',
    helpText: 'Sent to the customer but not opened yet.',
  },
  {
    id: 'engaged',
    label: 'Engaged',
    color: 'var(--color-primary-light)',
    helpText: 'Customer has viewed in the last 7 days. Stay close.',
  },
  {
    id: 'stalled',
    label: 'Stalled',
    color: 'var(--color-error)',
    helpText: 'No views in 7+ days. Needs follow-up.',
  },
  {
    id: 'interested',
    label: 'Interested',
    color: 'var(--color-warning)',
    helpText: 'Customer tapped "I’m Interested". Call list — confirm scope, then move to Won.',
  },
  {
    id: 'won',
    label: 'Won',
    color: 'var(--color-success)',
    helpText: 'Customer accepted. Hand off to onboarding.',
  },
  {
    id: 'lost',
    label: 'Lost',
    color: 'var(--color-text-light)',
    helpText: 'Expired or rejected. Re-quote candidates after a cooling period.',
  },
];

/** Days of inactivity before a viewed proposal counts as Stalled. */
export const STALLED_THRESHOLD_DAYS = 7;

// ---------------------------------------------------------------------------
// Follow-up temperature
//
// Not a measure of how interested the customer is, a measure of how long we
// have left this sitting. Green means nothing to do, yellow means a light
// personal check-in is due, red means find another way in or push harder.
// Same direction and the same pills as the property renewals temperature
// (see pos-properties/constants.tsx), on a different clock.
//
// The bands come from the win history: half of everything won was accepted
// within a week of being sent, nine in ten within three weeks, and nothing
// has ever been won after 45 days. Three bands by founder's call (2026-08-05),
// the fourth colour on the house scale, orange, is deliberately unused here.
//
// Anything settled has no temperature: accepted, lost, rejected, and anything
// not yet sent. There is nothing to chase on those.
// ---------------------------------------------------------------------------

export type ProposalTemperature = 'green' | 'yellow' | 'red';

export const PROPOSAL_TEMP_GREEN_MAX_DAYS = 7;
export const PROPOSAL_TEMP_YELLOW_MAX_DAYS = 30;

export const PROPOSAL_TEMPERATURE_CONFIG: Record<ProposalTemperature, {
  label: string;
  /** Shown on hover, tells the operator what the colour is asking for. */
  action: string;
}> = {
  green: { label: 'Green', action: 'Sent recently. Nothing to do yet.' },
  yellow: { label: 'Yellow', action: 'Going quiet. Worth a light personal check-in.' },
  red: { label: 'Red', action: 'Cold. Find another route in, or follow up harder.' },
};

interface TemperatureInput {
  status?: string;
  sent_at?: string | null;
  accepted_at?: string | null;
  lost_reason?: string | null;
  [key: string]: unknown;
}

/** Days since the proposal was sent, or null when it never was. */
export function proposalAgeDays(p: TemperatureInput): number | null {
  if (!p.sent_at) return null;
  return Math.floor((Date.now() - new Date(p.sent_at).getTime()) / 86400000);
}

export function computeProposalTemperature(p: TemperatureInput): ProposalTemperature | null {
  if (p.accepted_at || p.status === 'accepted') return null;
  if (p.status === 'expired' || p.status === 'rejected' || p.status === 'draft') return null;
  if (p.lost_reason) return null;

  const age = proposalAgeDays(p);
  if (age === null) return null;

  if (age <= PROPOSAL_TEMP_GREEN_MAX_DAYS) return 'green';
  if (age <= PROPOSAL_TEMP_YELLOW_MAX_DAYS) return 'yellow';
  return 'red';
}

interface BucketableProposal {
  status?: string;
  manual_stage?: string | null;
  submitted_for_approval_at?: string | null;
  reviewed_at?: string | null;
  sent_at?: string | null;
  viewed_at?: string | null;
  accepted_at?: string | null;
  interest_registered_at?: string | null;
  rejection_reason?: string | null;
  lost_reason?: string | null;
  expires_at?: string | null;
  [key: string]: unknown;
}

/**
 * Map a proposal to its workflow column id. Pure function — no side effects.
 *
 * Precedence (Phase v3 — user wins):
 *   1. Terminal: accepted → Won, expired → Lost (both set by user actions only)
 *   2. Approval workflow: pending_approval → Awaiting Review, draft/rejected → Drafting
 *   3. Manual stage override: if user pinned the card, that wins for the customer band
 *   4. Customer tap: interest_registered_at set → Interested
 *   5. Activity-derived: viewed_at recency → Engaged or Stalled, no view → Sent
 */
export function bucketProposal(p: BucketableProposal): string {
  // Terminal states (user-driven only — backend no longer auto-flips these)
  if (p.status === 'accepted' || p.accepted_at) return 'won';
  if (p.status === 'expired') return 'lost';

  // Pre-customer workflow
  if (p.status === 'pending_approval') return 'awaiting_review';
  if (p.status === 'draft' || p.status === 'rejected') return 'drafting';

  // User pin wins over derived placement in the customer-engagement band
  if (
    p.manual_stage === 'sent' ||
    p.manual_stage === 'engaged' ||
    p.manual_stage === 'stalled' ||
    p.manual_stage === 'interested'
  ) {
    return p.manual_stage;
  }

  // Customer tap on "I'm Interested" — stronger signal than passive view
  if (p.interest_registered_at) return 'interested';

  // Activity-derived placement for the customer-engagement band
  if (p.viewed_at) {
    const now = Date.now();
    const lastViewedMs = new Date(p.viewed_at).getTime();
    const daysSinceView = (now - lastViewedMs) / (1000 * 60 * 60 * 24);
    return daysSinceView <= STALLED_THRESHOLD_DAYS ? 'engaged' : 'stalled';
  }

  // Sent but never opened
  if (p.status === 'published') return 'sent';

  // Fallback — unrecognised status, surface in drafting so nothing disappears
  return 'drafting';
}

/**
 * Group an array of proposals by workflow column id.
 * Returns a Record keyed by column id, with empty arrays for columns
 * that have no proposals so the UI always renders all 7.
 */
export function groupProposalsByColumn<T extends BucketableProposal>(
  proposals: T[],
): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  WORKFLOW_COLUMNS.forEach((col) => { grouped[col.id] = []; });
  proposals.forEach((p) => {
    const colId = bucketProposal(p);
    (grouped[colId] ||= []).push(p);
  });
  return grouped;
}
